// Pure Similarity report layer (04j-a), ported VERBATIM from js/renderers-similarity.js —
// the templated "Notable differences / Age / Minutes caveat" report. No LLM: hand-written phrase
// templates over the same real per-axis comparison the score uses, just at a larger bar
// (NOTABLE_TOLERANCE_PCT). Kept pure (returns strings; the renderer paints them in 04j-c) so it's
// unit-tested. renderers-similarity.js §"Report generation".

import type { RawPlayerRecord } from "../data/snapshot.js";
import type { ProtoPlayerMetric } from "@catbento/shared";
import { axisRawValue, isLowerBetter } from "./radar-view.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { formatMetricValue } from "../engines/stats-filter-kit.js";
import { axisComparison, ageFromDob, type MetricRange, type SimilarityResult } from "./similarity-view.js";

/** Whether a missed axis is worth an actual report sentence — deliberately bigger than the score's
 *  4.5% match tolerance (SIMILARITY_ALGORITHM.md §6). */
export const NOTABLE_TOLERANCE_PCT = 0.15;
/** Smallest age gap before the "ceiling" framing can apply. */
export const CEILING_AGE_GAP = 4;
/** The minutes/appearances caveat fires when the smaller total is under this… */
export const MINUTES_FLOOR = 900;
/** …or the larger is this many × the smaller. */
export const MINUTES_RATIO = 3;

/** An approximate calendar reference for "how old were they in that season" — 5-digit "5"-prefixed
 *  codes (European domestic/continental) start ~Aug; plain 4-digit codes (NWSL) reference mid-year.
 *  renderers-similarity.js:32. */
export function seasonReferenceDate(seasonCode: string): Date | null {
  const str = String(seasonCode);
  if (str.length === 5 && str.charAt(0) === "5") {
    return new Date(parseInt(str.slice(1), 10), 7, 1); // Aug 1 of the season's start year
  }
  const year = parseInt(str, 10);
  if (Number.isNaN(year)) return null;
  return new Date(year, 6, 1); // Jul 1, a mid-season reference for a calendar-year league
}

/** The base player's age AT their selected (possibly historical) season. renderers-similarity.js:41. */
export function ageAtSeason(dob: string | null | undefined, seasonCode: string): number | null {
  if (!dob) return null;
  const ref = seasonReferenceDate(seasonCode);
  if (!ref) return null;
  const d = new Date(dob);
  let age = ref.getFullYear() - d.getFullYear();
  const monthDiff = ref.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < d.getDate())) age--;
  return age;
}

// Hand-written natural-reading sentences for the common default-template axes; anything else falls
// back to the generic builder using the metric's catalog name. renderers-similarity.js:307.
type PhraseFn = (l: string, t: string, lv: string, tv: string) => string;
const BESPOKE_AXIS_PHRASES: Record<string, PhraseFn> = {
  turnovers_90: (l, t, lv, tv) => `${l} gives the ball away far less often than ${t} (${lv} vs ${tv} turnovers per 90)`,
  np_xg_90: (l, t, lv, tv) => `${l} produces markedly more high-quality chances than ${t} (${lv} vs ${tv} non-penalty xG per 90)`,
  obv_pass_90: (l, t, lv, tv) => `${l}'s passing adds significantly more value than ${t}'s (${lv} vs ${tv} OBV from passing per 90)`,
  obv_90: (l, t, lv, tv) => `${l} contributes significantly more overall value on the ball than ${t} (${lv} vs ${tv} OBV per 90)`,
  padj_tackles_and_interceptions_90: (l, t, lv, tv) => `${l} makes far more defensive interventions than ${t} (${lv} vs ${tv} PAdj Tackles & Interceptions per 90)`,
  aerial_perc: (l, t, lv, tv) => `${l} wins a lot more of their aerial duels than ${t} (${lv} vs ${tv} aerial win rate)`,
  aerials_won_90: (l, t, lv, tv) => `${l} wins far more aerial duels than ${t} (${lv} vs ${tv} aerials won per 90)`,
  deep_progressions_90: (l, t, lv, tv) => `${l} progresses the ball into dangerous areas far more often than ${t} (${lv} vs ${tv} deep progressions per 90)`,
  fouls_committed_90: (l, t, lv, tv) => `${l} concedes far fewer fouls than ${t} (${lv} vs ${tv} fouls committed per 90)`,
  fouls_won_90: (l, t, lv, tv) => `${l} wins considerably more fouls than ${t} (${lv} vs ${tv} fouls won per 90)`,
  challenge_perc: (l, t, lv, tv) => `${l} wins a much higher share of their defensive challenges than ${t} (${lv} vs ${tv})`,
  np_shots_90: (l, t, lv, tv) => `${l} gets shots away far more often than ${t} (${lv} vs ${tv} non-penalty shots per 90)`,
  touches_in_box_90: (l, t, lv, tv) => `${l} gets into the penalty box far more often than ${t} (${lv} vs ${tv} touches in the box per 90)`,
  obv_dribble_carry_90: (l, t, lv, tv) => `${l} adds far more value carrying and dribbling than ${t} (${lv} vs ${tv} OBV from dribbles & carries per 90)`,
  op_xga_90: (l, t, lv, tv) => `${l} creates considerably more from open play than ${t} (${lv} vs ${tv} open-play xG assisted per 90)`,
  padj_clearances_90: (l, t, lv, tv) => `${l} clears the ball far more often than ${t} (${lv} vs ${tv} PAdj Clearances per 90)`,
};
function genericAxisSentence(leaderName: string, trailerName: string, metricName: string, leaderVal: string, trailerVal: string): string {
  return `${leaderName} has a notably higher ${metricName.toLowerCase()} than ${trailerName} (${leaderVal} vs ${trailerVal})`;
}

export interface DifferencesParams {
  base: RawPlayerRecord;
  baseSeason: string;
  candidate: RawPlayerRecord;
  candSeason: string;
  axisKeys: readonly string[];
  catalog: Record<string, ProtoPlayerMetric>;
  rangeByKey: Map<string, MetricRange | null>;
}

/** "Notable differences" sentences — missed axes past NOTABLE_TOLERANCE_PCT of the metric's real
 *  range, biggest gap first, polarity-aware "leader". renderers-similarity.js:329. */
export function buildDifferences({ base, baseSeason, candidate, candSeason, axisKeys, catalog, rangeByKey }: DifferencesParams): string[] {
  const out: { gapFrac: number; sentence: string }[] = [];
  for (const key of axisKeys) {
    const meta = catalog[key];
    if (!meta) continue;
    const baseRaw = axisRawValue(base, baseSeason, key);
    const candRaw = axisRawValue(candidate, candSeason, key);
    const cmp = axisComparison(baseRaw, candRaw, rangeByKey.get(key) ?? null, NOTABLE_TOLERANCE_PCT, isLowerBetter(key));
    if (!cmp || cmp.isMatch) continue; // not a real, notable gap
    const baseLeads = !cmp.candidateAhead;
    const leader = baseLeads ? base : candidate;
    const trailer = baseLeads ? candidate : base;
    const leaderRaw = baseLeads ? baseRaw : candRaw;
    const trailerRaw = baseLeads ? candRaw : baseRaw;
    const leaderName = getPlayerShortName(leader);
    const trailerName = getPlayerShortName(trailer);
    const leaderValFmt = formatMetricValue(leaderRaw, meta.outputType);
    const trailerValFmt = formatMetricValue(trailerRaw, meta.outputType);
    const phraseFn = BESPOKE_AXIS_PHRASES[key];
    const sentence = phraseFn
      ? phraseFn(leaderName, trailerName, leaderValFmt, trailerValFmt)
      : genericAxisSentence(leaderName, trailerName, meta.name, leaderValFmt, trailerValFmt);
    out.push({ gapFrac: cmp.span ? cmp.diff / cmp.span : 0, sentence });
  }
  out.sort((a, b) => b.gapFrac - a.gapFrac);
  return out.map((o) => o.sentence);
}

/** The always-present age sentence, with the "ceiling" framing when the younger player already
 *  matches the older one's profile (reusing the score's netAdvantage). renderers-similarity.js:379. */
export function buildAgeSentence(
  base: RawPlayerRecord,
  baseSeason: string,
  candidate: RawPlayerRecord,
  cmpResult: SimilarityResult | null,
): string | null {
  const baseAge = ageAtSeason(base.dob, baseSeason);
  const candAge = ageFromDob(candidate.dob);
  if (baseAge == null || candAge == null) return null;
  const baseName = getPlayerShortName(base);
  const candName = getPlayerShortName(candidate);
  if (baseAge === candAge) return `${baseName} and ${candName} are the same age (${baseAge}).`;
  const baseIsYounger = baseAge < candAge;
  const younger = baseIsYounger ? base : candidate;
  const older = baseIsYounger ? candidate : base;
  const youngerAge = baseIsYounger ? baseAge : candAge;
  const olderAge = baseIsYounger ? candAge : baseAge;
  const youngerName = getPlayerShortName(younger);
  const olderName = getPlayerShortName(older);
  const gap = olderAge - youngerAge;
  if (gap >= CEILING_AGE_GAP && cmpResult) {
    // netAdvantage > 0 means the CANDIDATE reads ahead overall; translate into "is the younger of
    // the two ahead-or-level."
    const youngerIsAheadOrLevel = baseIsYounger ? cmpResult.netAdvantage <= 0 : cmpResult.netAdvantage >= 0;
    if (youngerIsAheadOrLevel) {
      return `${youngerName}, at ${youngerAge}, already matches ${olderName}'s (${olderAge}) statistical profile on these axes — a promising sign for ${youngerName}'s ceiling.`;
    }
  }
  return `${youngerName} is ${gap} year${gap === 1 ? "" : "s"} younger than ${olderName} (${youngerAge} vs ${olderAge}).`;
}

/** The conditional minutes/appearances reliability caveat. renderers-similarity.js:410. */
export function buildMinutesCaveat(base: RawPlayerRecord, baseSeason: string, candidate: RawPlayerRecord): string | null {
  const baseMinutes = axisRawValue(base, baseSeason, "minutes");
  const candMinutes = axisRawValue(candidate, candidate.currentSeason ?? "", "minutes");
  if (baseMinutes == null || candMinutes == null) return null;
  const lo = Math.min(baseMinutes, candMinutes);
  const hi = Math.max(baseMinutes, candMinutes);
  if (lo >= MINUTES_FLOOR && hi / lo < MINUTES_RATIO) return null;
  const fewerIsBase = baseMinutes <= candMinutes;
  const fewer = fewerIsBase ? base : candidate;
  const fewerMin = Math.round(fewerIsBase ? baseMinutes : candMinutes).toLocaleString();
  const moreMin = Math.round(fewerIsBase ? candMinutes : baseMinutes).toLocaleString();
  return `${getPlayerShortName(fewer)} has played considerably fewer minutes this season (${fewerMin} vs ${moreMin}) — treat this comparison with some caution given the smaller sample.`;
}
